import { useEffect, useState, useRef } from "react";
import Swal from "sweetalert2";
import { RkflPlugin } from "@rkfl/transact-client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faTrash, faSpinner, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { environment } from "../../utils/constants";
export default function CheckoutPage() {
  const [cart, setCart] = useState([]);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [orderPrepared, setOrderPrepared] = useState(false);
  const invoiceCreatedRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate invoice creation (especially in React.StrictMode)
    if (invoiceCreatedRef.current) return;
    
    setLoading(true);
    renderCheckout();

    const payinFeature = { feature: "PAYIN", containerId: "payNow" };
    const env = localStorage.getItem("rkfl_environment") || "production";
    const plugin = new RkflPlugin({
      plugins: [payinFeature],
      environment: env,
      clientId: environment[env].clientId,
    });

    plugin.init().then(() => {
      // Always generate a new invoice/UUID - don't reuse stored UUID
      if (!invoiceCreatedRef.current) {
        invoiceCreatedRef.current = true;
        createInvoice(plugin);
      }
    });


    function handleMessage(event) {
      const success_event = "rocketfuel_result_ok";
      const data = event.data;
      if (data.type === success_event && data.paymentCompleted === 1) {
        Swal.fire({
          icon: "success",
          title: "Payment Successful!",
          timer: 1500,
          showConfirmButton: false,
        }).then(() => {
          const encoded = encodeURIComponent(JSON.stringify(event.data));
          if (typeof window !== "undefined") {
            navigate(`/success?data=${encoded}`);
          }
          localStorage.removeItem('cart');
        });
      }
    }

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      // Reset the ref when component unmounts
      invoiceCreatedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  function renderCheckout() {
    const raw = localStorage.getItem("cart");
    if (!raw) {
      setCart([]);
      setTotal(0);
      return;
    }
    try {
      const data = JSON.parse(raw);
      setCart(data.cart || []);
      const sum = (data.cart || []).reduce(
        (acc, item) => acc + item.quantity * parseFloat(item.price),
        0
      );
      setTotal(sum);
    } catch { }
  }

  async function createInvoice(plugin) {
    try {
      const raw = localStorage.getItem("cart");
      if (!raw) return;

      const cartObj = JSON.parse(raw);
      const env = localStorage.getItem("rkfl_environment") || "production";
      const credentials = { environment: env };

      cartObj.merchant_id = environment[env].merchantId;
      const completePayload = { credentials, payload: cartObj };

      const url = `https://rocketfuel-merchant.netlify.app/api/invoice`;
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(completePayload),
      });

      const data = await response.json();

      if (data?.result?.uuid) {
        // Use the UUID directly without storing in localStorage
        plugin.prepareOrder(data.result.uuid);
        setLoading(false);
        setOrderPrepared(true);
      } else {
        setLoading(false);
        Swal.fire({
          icon: "error",
          title: "Invoice Error",
          text: "Could not generate invoice.",
        });
      }
    } catch (err) {
      setLoading(false);
      Swal.fire({
        icon: "error",
        title: "Request Failed",
        text: String(err),
      });
    }
  }


  function clearCart() {
    localStorage.removeItem("cart");
    setCart([]);
    setTotal(0);
    Swal.fire({
      icon: "info",
      title: "Cart Cleared",
      timer: 1200,
      showConfirmButton: false,
    });
  }

  return (
    <div>
      <nav className="navbar">
        <div className="brand-title">🎬 StreamFlix</div>
      </nav>

      <div className="container my-6">
        <div className="checkout-main-card">
          <div className="mb-3">
            <button
              onClick={() => navigate("/")}
              className="btn btn-outline-primary rounded-pill px-4 font-medium"
            >
              <FontAwesomeIcon icon={faArrowLeft} /> &nbsp;Back to Home
            </button>
          </div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h2 className="section-title">Checkout</h2>
            <button className="clear-btn" onClick={clearCart}>
              <FontAwesomeIcon icon={faTrash} /> &nbsp;Clear Cart
            </button>
          </div>

          {cart.length === 0 ? (
            <div className="alert alert-warning text-center">No cart found.</div>
          ) : (
            <>
              <table className="table checkout-table">
                <thead>
                  <tr>
                    <th className="text-left">Item</th>
                    <th>Quantity</th>
                    <th>Price</th>
                    <th>Sub-total</th>
                  </tr>
                </thead>
                <tbody>
                  {cart.map((item) => (
                    <tr key={item.id}>
                      <td className="text-left">{item.name}</td>
                      <td>{item.quantity}</td>
                      <td>${parseFloat(item.price).toFixed(2)}</td>
                      <td>
                        ${(item.quantity * parseFloat(item.price)).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3} className="text-end checkout-total-row">
                      Total
                    </td>
                    <td className="checkout-total-row">
                      ${total.toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
              {loading && (
                  <div
                    style={{
                      background: '#fff9e6',
                      borderRadius: '12px',
                      padding: '24px 32px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(255, 193, 7, 0.15)',
                      border: '1px solid #ffc107',
                      marginBottom: '20px',
                    }}
                  >
                    <div style={{ marginBottom: '16px' }}>
                      <FontAwesomeIcon
                        icon={faSpinner}
                        spin
                        style={{
                          fontSize: '32px',
                          color: '#ff9800',
                        }}
                      />
                    </div>
                    <h5
                      style={{
                        color: '#856404',
                        fontWeight: '600',
                        marginBottom: '8px',
                        fontSize: '1.1rem',
                      }}
                    >
                      Initializing Payment Gateway
                    </h5>
                    <p
                      style={{
                        color: '#856404',
                        margin: 0,
                        fontSize: '0.9rem',
                        textAlign: 'center',
                        lineHeight: '1.5',
                      }}
                    >
                      Please wait while we prepare your secure payment environment...
                    </p>
                    <div
                      style={{
                        marginTop: '12px',
                        fontSize: '0.85rem',
                        color: '#856404',
                        fontStyle: 'italic',
                        opacity: 0.8,
                      }}
                    >
                      This may take a few moments
                    </div>
                  </div>
                )}
              {orderPrepared && !loading && (
                  <div
                    style={{
                      background: '#e3f2fd',
                      borderRadius: '12px',
                      padding: '24px 32px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(33, 150, 243, 0.2)',
                      border: '1px solid #2196f3',
                      marginBottom: '20px',
                    }}
                  >
                    <div style={{ marginBottom: '16px' }}>
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        style={{
                          fontSize: '36px',
                          color: '#1976d2',
                        }}
                      />
                    </div>
                    <h5
                      style={{
                        color: '#1565c0',
                        fontWeight: '600',
                        marginBottom: '12px',
                        fontSize: '1.15rem',
                      }}
                    >
                      Order Ready!
                    </h5>
                    <p
                      style={{
                        color: '#1565c0',
                        margin: 0,
                        fontSize: '0.95rem',
                        textAlign: 'center',
                        lineHeight: '1.6',
                        marginBottom: '16px',
                      }}
                    >
                      Your payment gateway is now ready. Please click the button below to proceed with checkout.
                    </p>
                  </div>
                )}
              <div id="payNow" className="mt-3">
        
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
